import BusinessList from '@/components/business/BusinessList';
import DownloadApp from '@/components/shared/DownloadApp';
import { ENV } from '@/core/config/env';
import { GET_BUSINESS_LIST } from '@/core/graphql/queries/getBusinessList';
import { GET_CITIES } from '@/core/graphql/queries/getCities';
import { BusinessItem } from '@/core/models/businesses/businessList';
import { SearchBusinessRequest } from '@/core/models/businesses/businessRequest';
import { City } from '@/core/models/locations/city';
import { initializeApollo } from '@/lib/apolloClient';
import { slugify } from '@/lib/slugify';
import Head from 'next/head';
import Link from 'next/link';
import { GetStaticPaths, GetStaticProps } from 'next';

const CAR_PARTS_CATEGORY_ID = '2012';

// Top SA makes/models to pre-generate at build time
const POPULAR_CAR_PATHS = [
    { make: 'Toyota', model: 'Corolla' },
    { make: 'Toyota', model: 'Hilux' },
    { make: 'Toyota', model: 'Fortuner' },
    { make: 'Toyota', model: 'Prado' },
    { make: 'Toyota', model: 'RAV4' },
    { make: 'Volkswagen', model: 'Golf' },
    { make: 'Volkswagen', model: 'Polo' },
    { make: 'Volkswagen', model: 'Tiguan' },
    { make: 'Ford', model: 'Ranger' },
    { make: 'Ford', model: 'Fiesta' },
    { make: 'Ford', model: 'Focus' },
    { make: 'BMW', model: '3 Series' },
    { make: 'BMW', model: '5 Series' },
    { make: 'BMW', model: 'X5' },
    { make: 'Mercedes', model: 'C-Class' },
    { make: 'Mercedes', model: 'E-Class' },
    { make: 'Hyundai', model: 'Tucson' },
    { make: 'Hyundai', model: 'i20' },
    { make: 'Nissan', model: 'Navara' },
    { make: 'Nissan', model: 'X-Trail' },
    { make: 'Mazda', model: 'Mazda 3' },
    { make: 'Honda', model: 'Civic' },
    { make: 'Kia', model: 'Sportage' },
    { make: 'Isuzu', model: 'D-Max' },
    { make: 'Mitsubishi', model: 'Pajero' },
];

const TOP_CITIES = [
    'Johannesburg',
    'Cape Town',
    'Durban',
    'Pretoria',
    'Gqeberha',
    'Bloemfontein',
    'East London',
    'Polokwane',
    'Nelspruit',
    'Roodepoort',
    'Benoni',
    'Boksburg',
    'Germiston',
    'Randburg',
    'Sandton',
];

interface Props {
    businesses: BusinessItem[];
    pagination: {
        count: number;
        currentPage: number;
        totalPages: number;
    };
    make: string;
    makeDisplay: string;
    model: string;
    modelDisplay: string;
    city: string;
    cityName: string;
}

export default function CarPartsMakeModelCityPage({
    businesses,
    pagination,
    make,
    makeDisplay,
    model,
    modelDisplay,
    city,
    cityName,
}: Props) {
    const searchParams = {
        category: CAR_PARTS_CATEGORY_ID,
        text: `${makeDisplay} ${modelDisplay}`,
        city,
        province: '',
        suburb: '',
        page: 1,
    };

    const pageTitle = `${makeDisplay} ${modelDisplay} Car Parts in ${cityName} | Car Spares EzyFind`;
    const pageDescription = `Find ${makeDisplay} ${modelDisplay} car parts and spares in ${cityName}. Browse trusted suppliers of new and used ${makeDisplay} ${modelDisplay} parts near you. Get quotes, compare prices and buy online.`;
    const canonicalUrl = `${ENV.DOMAIN_URL}car-parts/${make}/${model}/${city}/`;

    const keywords = [
        `${makeDisplay} ${modelDisplay} parts ${cityName}`,
        `${makeDisplay} ${modelDisplay} spares ${cityName}`,
        `buy ${makeDisplay} ${modelDisplay} parts South Africa`,
        `${makeDisplay} ${modelDisplay} car parts near me`,
        `auto spares ${cityName}`,
        `car parts ${cityName}`,
        `${makeDisplay} spare parts South Africa`,
    ].join(', ');

    const faqItems = [
        {
            question: `Where can I find ${makeDisplay} ${modelDisplay} parts in ${cityName}?`,
            answer: `Use our directory to find trusted suppliers of ${makeDisplay} ${modelDisplay} spares and car parts in ${cityName}. Browse profiles, compare suppliers, and contact them directly.`,
        },
        {
            question: `Are new and used ${makeDisplay} ${modelDisplay} parts available in ${cityName}?`,
            answer: `Yes. Suppliers in ${cityName} offer both new OEM parts, aftermarket parts, and quality used spares for the ${makeDisplay} ${modelDisplay}.`,
        },
        {
            question: `Can I compare ${makeDisplay} ${modelDisplay} parts suppliers in ${cityName}?`,
            answer: 'Yes. You can shortlist up to 2 suppliers, review their services and locations, and contact multiple suppliers before making a decision.',
        },
    ];

    const breadcrumbs = [
        { label: 'Car Parts', href: '/car-parts-spares-near-me' },
        { label: makeDisplay, href: `/car-parts/${make}/` },
        { label: modelDisplay, href: `/car-parts/${make}/${model}/` },
        { label: cityName, href: `/car-parts/${make}/${model}/${city}/` },
    ];

    const breadcrumbSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((crumb, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: crumb.label,
            item: `${ENV.DOMAIN_URL}${crumb.href.replace(/^\//, '')}`,
        })),
    };

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta name="keywords" content={keywords} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:type" content="website" />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
                />
            </Head>

            {/* Hero */}
            <section className="relative overflow-hidden bg-[#0f1512] text-white">
                <div
                    className="absolute inset-0 opacity-35"
                    style={{
                        backgroundImage:
                            'radial-gradient(circle at 15% 20%, rgba(220, 38, 38, 0.35), transparent 45%), radial-gradient(circle at 80% 10%, rgba(239, 68, 68, 0.3), transparent 40%)',
                    }}
                />
                <div className="container relative py-12 md:py-16">
                    <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
                        <div className="lg:col-span-7">
                            <nav className="text-sm text-red-200 mb-3">
                                {breadcrumbs.map((crumb, i) => (
                                    <span key={crumb.href}>
                                        {i > 0 && <span className="mx-1">&gt;</span>}
                                        {i < breadcrumbs.length - 1 ? (
                                            <Link href={crumb.href} className="hover:text-white">
                                                {crumb.label}
                                            </Link>
                                        ) : (
                                            <span className="text-white">{crumb.label}</span>
                                        )}
                                    </span>
                                ))}
                            </nav>
                            <p className="text-xs uppercase tracking-[0.3em] text-red-200">Car Parts Directory</p>
                            <h1 className="mt-3 text-3xl md:text-5xl font-bold leading-tight">
                                {makeDisplay} {modelDisplay} Parts in {cityName}
                            </h1>
                            <p className="mt-4 text-base md:text-lg text-red-100">
                                Find quality {makeDisplay} {modelDisplay} car parts and auto spares from trusted suppliers
                                in {cityName}, South Africa.
                            </p>
                            <div className="mt-6 flex flex-wrap items-center gap-3">
                                <Link
                                    href="#business-filters"
                                    className="bg-white text-slate-900 px-5 py-2.5 rounded-md text-sm font-semibold shadow"
                                >
                                    Find Suppliers
                                </Link>
                                <Link
                                    href="/car-parts-spares-near-me"
                                    className="border border-white/30 text-white px-5 py-2.5 rounded-md text-sm font-semibold"
                                >
                                    All Car Parts
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Business Listing */}
            <div className="my-10" id="business-filters">
                <div className="container">
                    <BusinessList
                        searchParams={searchParams}
                        initialBusinesses={businesses}
                        initialPagination={pagination}
                    />
                </div>
            </div>

            {/* Info + FAQ */}
            <section className="container my-12">
                <div className="grid gap-8 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <p className="text-xs uppercase tracking-[0.25em] text-red-700">Car Parts Directory</p>
                            <h2 className="mt-3 text-2xl font-semibold text-slate-900">
                                {makeDisplay} {modelDisplay} Spares in {cityName}
                            </h2>
                            <p className="mt-3 text-sm text-slate-600">
                                Find local suppliers of {makeDisplay} {modelDisplay} car parts in {cityName}. Browse new,
                                used, OEM, and aftermarket spares from trusted automotive businesses.
                            </p>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {['New OEM Parts', 'Used Spares', 'Aftermarket', 'Engine Parts', 'Body Panels', 'Electrical'].map(
                                    (tag) => (
                                        <span
                                            key={tag}
                                            className="rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-700"
                                        >
                                            {tag}
                                        </span>
                                    ),
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="text-lg font-semibold text-slate-900">Frequently Asked Questions</h2>
                            <div className="mt-4 space-y-4">
                                {faqItems.map((item) => (
                                    <div key={item.question} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                        <h3 className="text-sm font-semibold text-slate-900">{item.question}</h3>
                                        <p className="mt-2 text-sm text-slate-600">{item.answer}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <DownloadApp />
        </>
    );
}

export const getStaticPaths: GetStaticPaths = async () => {
    const apolloClient = initializeApollo();

    const { data: cityData } = await apolloClient.query({ query: GET_CITIES });
    const cities: City[] = cityData?.getCity?.result || [];

    const includedCities = cities
        .filter((c: City) => c.include === true || c.include === 1)
        .filter((c: City) => TOP_CITIES.includes(c.cityName));

    const paths: { params: { make: string; model: string; city: string } }[] = [];

    for (const entry of POPULAR_CAR_PATHS) {
        for (const city of includedCities) {
            paths.push({
                params: {
                    make: slugify(entry.make),
                    model: slugify(entry.model),
                    city: slugify(city.cityName),
                },
            });
        }
    }

    return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
    const apolloClient = initializeApollo();

    const makeSlug = params?.make as string;
    const modelSlug = params?.model as string;
    const citySlug = params?.city as string;

    const { data: cityData } = await apolloClient.query({ query: GET_CITIES });
    const cities: City[] = cityData?.getCity?.result || [];

    const city = cities.find((c: City) => slugify(c.cityName) === citySlug);
    if (!city) {
        return { notFound: true };
    }

    // Convert slug back to display name
    const makeDisplay = makeSlug
        .split('-')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
    const modelDisplay = modelSlug
        .split('-')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');

    const businessRequest: SearchBusinessRequest = {
        statusIds: '1',
        domainId: Number(ENV.CATEGORY_ID),
        categoryIds: CAR_PARTS_CATEGORY_ID,
        provinceIds: '',
        cityIds: String(city.cityId),
        suburbIds: '',
        companyName: '',
        page: 1,
        size: 12,
    };

    const { data: businessData } = await apolloClient.query({
        query: GET_BUSINESS_LIST,
        variables: businessRequest,
    });

    const isStaticExport = process.env.BUILD_MODE === 'export';

    return {
        props: {
            businesses: businessData?.getBusinessList?.result || [],
            pagination: {
                count: businessData?.getBusinessList?.count ?? 0,
                currentPage: businessData?.getBusinessList?.currentPage ?? 1,
                totalPages: businessData?.getBusinessList?.totalPages ?? 0,
            },
            make: makeSlug,
            makeDisplay,
            model: modelSlug,
            modelDisplay,
            city: citySlug,
            cityName: city.cityName,
        },
        ...(!isStaticExport && { revalidate: 3600 }),
    };
};
